import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';

import type { ApiErrorType, ServerError } from 'models';
import { handleSnackbarError, useSnackbarStore } from 'stores/zustand/snackbarStore';
import { getErrorMessage } from 'utils/errors';

import { parseError } from './parseError';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong, please try again.';

export type ErrorNotification =
    | { key: string; message: string; serverError: ServerError; type: 'server' }
    | { key: string; message: string; type: 'client' };

// Some mutations reject with an already-parsed ServerError rather than the AxiosError — the
// transport layer's onErr receives parseError's output, and callers like useMfaVerificationMutation
// forward it straight to reject. It is a plain object, so neither isAxiosError nor instanceof Error
// catches it. Match on parseError's own shape (nested `error` plus its status booleans) so a bare
// ApiErrorType, which has the nested fields but none of the flags, doesn't qualify.
function isServerError(error: unknown): error is ServerError {
    if (typeof error !== 'object' || error === null) return false;
    const candidate = error as Partial<ServerError>;
    return (
        typeof candidate.error === 'object' &&
        candidate.error !== null &&
        typeof candidate.cancelled === 'boolean' &&
        typeof candidate.unauthorized === 'boolean'
    );
}

export function getErrorNotification(
    error: unknown,
    options: { fallbackMessage?: string } = {},
): ErrorNotification | null {
    if (!error) return null;

    if (isAxiosError<ApiErrorType>(error)) {
        const serverError = parseError(error as AxiosError<ApiErrorType>);
        const message = getErrorMessage(serverError);
        return { key: `server:${message}`, message, serverError, type: 'server' };
    }

    if (isServerError(error)) {
        const message = getErrorMessage(error);
        return { key: `server:${message}`, message, serverError: error, type: 'server' };
    }

    const message = error instanceof Error ? error.message : (options.fallbackMessage ?? DEFAULT_ERROR_MESSAGE);
    return { key: `client:${message}`, message, type: 'client' };
}

/**
 * Display copy for a rejected promise. React Query mutations reject with either the raw AxiosError
 * or an already-parsed ServerError — the former is an Error, so `err instanceof Error ? err.message`
 * yields "Request failed with status code 400"; the latter is a plain object, so the same expression
 * yields the fallback. Both lose the backend's copy. Use this anywhere a caught error is displayed.
 */
export function getDisplayErrorMessage(error: unknown, fallbackMessage?: string): string {
    return getErrorNotification(error, { fallbackMessage })?.message ?? fallbackMessage ?? DEFAULT_ERROR_MESSAGE;
}

export function showErrorNotification(notification: ErrorNotification): void {
    if (notification.type === 'server') {
        handleSnackbarError(notification.serverError);
        return;
    }

    useSnackbarStore.getState().actions.pushMessage({ text: notification.message, variant: 'negative' });
}
